// Rate limiting queue for cart attribute updates
const cartAttributeQueue = [];
let isProcessingCartAttribute = false;
const currentDelay = 500;
const BATCH_WINDOW = 250; // Wait to collect multiple requests
let batchTimeout = null;

const getCookieValue = (name) => {
  const cookieString = `; ${document.cookie}`;
  const parts = cookieString.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
};

const setCookieValue = (name, value, expires) => {
  const date = new Date();
  date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
  const expiresString = `; expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}${expiresString}; path=/`;
};

const processCartAttributeQueue = async () => {
  if (isProcessingCartAttribute || cartAttributeQueue.length === 0) {
    return;
  }

  isProcessingCartAttribute = true;

  // Snapshot current batch so each enqueued promise can be resolved/rejected.
  const batch = [];
  const attributes = {};
  while (cartAttributeQueue.length > 0) {
    const item = cartAttributeQueue.shift();
    batch.push(item);
    attributes[item.key] = item.value;
  }

  try {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      },
    );

    if (!response.ok) {
      throw response;
    }

    const data = await response.json();
    batch.forEach(({ resolve }) => resolve(data));
  } catch (error) {
    console.error('Error setting cart attributes:', error);
    batch.forEach(({ reject }) => reject(error));
  }

  setTimeout(() => {
    isProcessingCartAttribute = false;
    processCartAttributeQueue();
  }, currentDelay);
};

const setCartAttribute = async (key, value) => {
  if (typeof key !== 'string' || !key) {
    throw new Error('Cart attribute key must be a non-empty string');
  }

  return new Promise((resolve, reject) => {
    cartAttributeQueue.push({ key, value, resolve, reject });

    // Clear existing batch window and set a new one
    clearTimeout(batchTimeout);
    batchTimeout = setTimeout(() => {
      processCartAttributeQueue();
    }, BATCH_WINDOW);
  });
};

const getSectionId = (section) => {
  if (!section) return '';

  if (section.dataset.sectionId) {
    return section.dataset.sectionId;
  }

  if (section.id && section.id.startsWith('shopify-section-')) {
    return section.id.replace('shopify-section-', '');
  }

  return '';
};

const getSectionFromHTML = (html, sectionId) => {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.querySelector(`#shopify-section-${sectionId}`);
};

class UrlDiscountModule {
  constructor() {
    this.cart = null;
    this.discountCodes = [];
    this.hasCartItems = false;
    this.discountAttribute = '';
    this.urlDiscountCookie = getCookieValue('discount_code');
    const urlParams = new URLSearchParams(window.location.search);
    this.urlDiscountValue = Number(
      urlParams.get('utm_term') || getCookieValue('url_discount_value'),
    );
    this.existingCustomerCode = urlParams.get('existing_customer_code');
  }

  async init() {
    // Get Cart Data
    try {
      this.cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((response) => response.json());

      if (this.cart && Array.isArray(this.cart.discount_codes)) {
        this.discountCodes = this.cart.discount_codes;
        this.hasCartItems = !!this.cart.items.length;
        this.discountAttribute = this.cart.attributes?.discount_code || '';
      }
    } catch (error) {
      console.error('Error retrieving discount codes:', error);
    }

    if (this.existingCustomerCode) {
      await this.handleExistingCustomerDiscount();
    }

    await this.handleDiscount();
  }

  async handleDiscount() {
    if (!this.urlDiscountCookie && !this.discountAttribute) {
      return;
    }

    if (!this.discountCodes.length || !this.urlDiscountCookie || !this.urlDiscountValue) {
      const didClearDiscount = await this.clearDiscountFromAttributes();

      if (didClearDiscount) {
        await this.renderSections();
      }

      return;
    }

    setCookieValue('url_discount_value', this.urlDiscountValue, 1);

    this.localCode = {
      code: this.urlDiscountCookie.toUpperCase(),
      value: this.urlDiscountValue,
    };

    const matchingDiscount = this.discountCodes?.find(
      (discount) => discount.code.toUpperCase() === this.localCode.code,
    );

    this.isCodeAlreadyApplied = !!matchingDiscount;
    this.isDiscountApplicable = matchingDiscount?.applicable === true || (!this.hasCartItems && this.isCodeAlreadyApplied);

    const attributeValue = this.getDiscountAttributeValue();

    if ((this.isCodeAlreadyApplied
      && this.isDiscountApplicable
      && this.discountAttribute === attributeValue)
      || (!this.isCodeAlreadyApplied && this.hasCartItems && !this.isDiscountApplicable)
    ) {
      return;
    }

    if (this.isCodeAlreadyApplied && this.hasCartItems && !this.isDiscountApplicable) {
      const didClearDiscount = await this.clearDiscountFromAttributes();

      if (didClearDiscount) {
        await this.renderSections();
      }

      return;
    }

    if ((!this.isCodeAlreadyApplied)
      || (this.isCodeAlreadyApplied && this.discountAttribute !== attributeValue)) {
      await this.addDiscountToAttributes();
      await this.renderSections();
    }
  }

  async clearDiscountFromAttributes() {
    if (!this.discountAttribute) {
      return false;
    }

    try {
      await Promise.all([
        setCartAttribute('discount_code', ''),
        setCartAttribute('dh-new-customer-code', ''),
        setCartAttribute('dh-existing-customer-code', ''),
      ]);
      setCookieValue('url_discount_value', '', 1);
      this.discountAttribute = '';

      return true;
    } catch (error) {
      console.error('Error clearing discount from cart attributes:', error);
    }

    return false;
  }

  getDiscountAttributeValue() {
    const discountValue = typeof this.localCode.value === 'number' && !Number.isNaN(this.localCode.value)
      ? this.localCode.value / 100
      : '';

    return `${this.localCode.code}:${discountValue}`;
  }

  async addDiscountToAttributes() {
    const attributeValue = this.getDiscountAttributeValue();

    try {
      await setCartAttribute('discount_code', attributeValue);
      this.discountAttribute = attributeValue;
    } catch (error) {
      console.error('Error adding discount to cart attributes:', error);
    }
  }

  async renderSections() {
    const sections = Array.from(document.querySelectorAll('.shopify-section'))
      .filter((section) => section.querySelector('price-list, product-card, .product-info'));

    const sectionIds = [...new Set(sections.map(getSectionId).filter(Boolean))];

    if (!sectionIds.length) {
      return;
    }

    try {
      const results = await Promise.all(sectionIds.map(async (sectionId) => {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) {
          return { sectionId, html: '' };
        }

        return { sectionId, html: await response.text() };
      }));

      results.forEach(({ sectionId, html }) => {
        if (!html) return;

        const currentSection = document.getElementById(`shopify-section-${sectionId}`);
        if (!currentSection) return;

        const renderedSection = getSectionFromHTML(html, sectionId);

        if (renderedSection) {
          currentSection.replaceWith(renderedSection);
        } else {
          currentSection.innerHTML = html;
        }
      });
    } catch (error) {
      console.error('Error rendering URL discount sections:', error);
    }
  }

  async handleExistingCustomerDiscount() {
    try {
      await Promise.all([
        setCartAttribute('dh-new-customer-code', this.urlDiscountCookie),
        setCartAttribute('dh-existing-customer-code', this.existingCustomerCode),
      ]);
    } catch (error) {
      console.error(
        'Error adding existing customer discount to cart attributes:',
        error,
      );
    }
  }
}

const urlDiscountInstance = new UrlDiscountModule();

if (document.readyState !== 'complete') {
  document.addEventListener('DOMContentLoaded', async () => {
    urlDiscountInstance.init();
  });
} else {
  urlDiscountInstance.init();
}
